import React from 'react'
import { Briefcase, Calendar1, Contact } from 'lucide-react'
import { Flex, Text, useAppTheme } from '@workwhile/ui'
import { CopyableLink } from 'components/Shared/CopyableLink'
import { FlexibleInfoTable } from 'pages/Shifts/ShiftDetail/components/FlexibleInfoTable'
import {
  UserPaymentEventSubType,
  UserPaymentEventType,
} from 'api/payments/getUserPayments'
import { toTitleCase } from 'lib/string'
import { getShiftCalendarLines } from './formatPaymentTimestamp'

export type BundledPaymentDetailsProps = {
  eventType: UserPaymentEventType
  shiftId?: number | null
  shiftStartsAt?: string | null
  shiftEndsAt?: string | null
  positionId?: number | null
  positionName?: string | null
  companyId?: number | null
  companyName?: string | null
  eventSubType?: UserPaymentEventSubType | null
  showShiftContext?: boolean
}

export const hasBundledPaymentDetails = ({
  shiftId,
  shiftStartsAt,
  shiftEndsAt,
  positionId,
  positionName,
  companyId,
  companyName,
  eventSubType,
  showShiftContext = true,
}: BundledPaymentDetailsProps): boolean => {
  if (!showShiftContext) {
    return Boolean(eventSubType)
  }

  return Boolean(
    (positionName && positionId) ||
      (companyName && companyId) ||
      (shiftStartsAt && shiftEndsAt)
  )
}

export const BundledPaymentDetails = ({
  shiftId,
  shiftStartsAt,
  shiftEndsAt,
  positionId,
  positionName,
  companyId,
  companyName,
  eventSubType,
  showShiftContext = true,
  ...rest
}: BundledPaymentDetailsProps) => {
  const { space } = useAppTheme()
  const shiftCalendarLines =
    shiftStartsAt && shiftEndsAt
      ? getShiftCalendarLines(shiftStartsAt, shiftEndsAt)
      : null

  if (
    !hasBundledPaymentDetails({
      shiftId,
      shiftStartsAt,
      shiftEndsAt,
      positionId,
      positionName,
      companyId,
      companyName,
      eventSubType,
      showShiftContext,
      ...rest,
    })
  ) {
    return null
  }

  if (!showShiftContext && eventSubType) {
    return (
      <FlexibleInfoTable Icon={Briefcase}>
        <Text fontSize={1} color="lightText">
          Sub type: {toTitleCase(eventSubType)}
        </Text>
      </FlexibleInfoTable>
    )
  }

  return (
    <Flex flexDirection="column" style={{ gap: space[1] }}>
      {positionName && positionId ? (
        <FlexibleInfoTable Icon={Contact}>
          <Text fontSize={1} color="lightText">
            {positionName} (
            <CopyableLink
              label={positionId}
              link={`/position_editor?positionId=${positionId}`}
            />
            )
          </Text>
        </FlexibleInfoTable>
      ) : null}

      {companyName && companyId ? (
        <FlexibleInfoTable Icon={Briefcase}>
          <Text fontSize={1} color="lightText">
            {companyName} (
            <CopyableLink label={companyId} link={`/companies/${companyId}`} />)
          </Text>
        </FlexibleInfoTable>
      ) : null}

      {shiftCalendarLines ? (
        <FlexibleInfoTable Icon={Calendar1}>
          <Text fontSize={1} color="lightText">
            {shiftCalendarLines.day} ({shiftCalendarLines.dayDistance})
          </Text>
          <Text fontSize={1} color="lightText">
            {shiftCalendarLines.timeRange}
          </Text>
        </FlexibleInfoTable>
      ) : null}
    </Flex>
  )
}
